import React, { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Mail, Lock, LogIn, Eye, EyeOff } from 'lucide-react'
import { Input } from '@/components/ui_custom/input'
import { Button } from '@/components/ui_custom/button'
import { Logo } from '@/components/common/logo'
import { useAuthStore } from '@/store/auth'
import { loginSchema, LoginSchema, UserProps } from '@/entities/user'
import { Form, FormField, FormItem, FormControl, FormMessage } from '@/components/ui/form'
import { useToast } from '@/hooks/use-toast'
import { HTTP_STATUS_CODE } from '@/constants/http'
import { api } from '@/lib/axios'
import { initChatStore } from '@/store/chat'
import { useLoader } from '@/contexts/LoaderContext'
import { CustomDialog } from '@/components/dialogs/custom-dialog'
import ProductCard from '@/components/cards/product'

// variables
const loc = '@/pages/Login'

export default function Login() {
  const { login, isAuthenticated } = useAuthStore()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { showLoader, hideLoader, isLoading } = useLoader()
  const location = useLocation()
  const [isShowingPassword, setIsShowingPassword] = useState(false)
  const [isShowingSignupModal, setIsShowingSignupModal] = useState(false)
  const fromPathname = location.state?.from?.pathname || '/'
  const [env, setEnv] = useState(import.meta.env.VITE_NODE_ENV)
  const products = [
    /* {
      id: 1,
      name: 'Mãe Nutrida - primeiros passos',
      image: '/images/products/pp.jpeg',
      link: 'https://go.hotmart.com/K85918624B?dp=1'
    }, */
    {
      id: 2,
      name: 'Mãe Nutrida Plus',
      image: '/images/products/plus.jpeg',
      link: 'https://maenutrida.com.br/materno-lista-de-espera/'
    },
    {
      id: 3,
      name: 'MENTORIA PRIME - Camila Komatsu',
      image: '/images/products/mentoria.jpeg',
      link: 'https://maenutrida.com.br/mentoria-prime/'
    }
    /* {
      id: 4,
      name: 'Mãe Nutrida Club',
      image: '/images/products/club.jpeg',
      link: 'https://go.hotmart.com/C99840547G?dp=1'
    } */
  ]

  const form = useForm<LoginSchema>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: import.meta.env.VITE_NODE_ENV === 'development' ? 'admin@gmail.com' : '',
      password: import.meta.env.VITE_NODE_ENV === 'development' ? 'teste' : ''
    }
  })

  const formValues = form.watch()

  async function handleLogin() {
    try {
      showLoader()

      const emailResponse = await api.get(`/`, { params: { email: formValues.email?.toLowerCase()?.trim(), action: 'user-by-email' } })

      if (emailResponse.data?.statusCode === HTTP_STATUS_CODE.OK) {
        const { local, hotmart } = emailResponse.data.body[0]

        if (!local && !hotmart) {
          hideLoader()
          toast({ variant: 'destructive', title: '', description: emailResponse.data?.details || 'Não foi possível validar o e-mail.' })
          setIsShowingSignupModal(true)
          return
        }

        if (!local && hotmart) {
          navigate('/register', { state: { email: form.getValues().email?.toLowerCase()?.trim() } })
          toast({ variant: 'default', title: 'Ops ...', description: emailResponse.data?.details || 'Realize o cadastro.' })
          return
        }
      } else {
        toast({ variant: 'destructive', title: 'Ops ...', description: emailResponse.data?.statusMessage || 'Não foi possível validar o e-mail.' })
        return
      }

      const body = { email: formValues.email?.toLowerCase()?.trim(), password: formValues.password || '********', action: 'login' }
      const response = await api.post(`/auth`, body)

      if (response.data?.statusCode === HTTP_STATUS_CODE.OK) {
        const { user }: { user: UserProps } = response.data.body[0]
        const token = response.data.token
        login(user, token)
        initChatStore(user)

        if (fromPathname != '/') {
          window.location.href = fromPathname
        } else {
          window.location.href = '/chat'
        }
      } else toast({ variant: 'destructive', title: 'Ops ...', description: response.data?.statusMessage || 'Credenciais incorretas.' })
    } catch (err) {
      console.error(`Unhandled rejection at ${loc}.handleLogin function. Details: ${err}`)
      toast({ variant: 'destructive', title: 'Ops ...', description: 'Não foi possível realizar o login.' })
    } finally {
      hideLoader()
    }
  }

  useEffect(() => {
    if (isAuthenticated) window.location.href = '/chat'
  }, [isAuthenticated])

  return (
    <div className="app-shell min-h-screen min-w-full flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-8 bg-card p-8 rounded-3xl shadow-elevated border border-border/50">
        <div className="flex justify-center items-center cursor-pointer" onClick={() => (window.location.href = '/')}>
          <Logo size="md" invert={true} />
        </div>

        <h1 className="mt-6 text-center text-xl font-bold text-foreground">
          Entre na sua conta {env == 'development' && 'DEV'} {env == 'hml' && 'HML'}
        </h1>
        <Form {...form}>
          <form
            className="w-full disabled:cursor-not-allowed flex flex-col gap-6"
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleLogin()
              }
            }}
          >
            <div className="space-y-4">
              <FormField
                control={form.control}
                name={'email'}
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-2">
                    <FormControl>
                      <Input {...field} id="email" name="email" type="email" autoComplete="email" required label="E-mail" disabled={isLoading} labelClass="text-foreground font-medium" leftIcon={<Mail className="h-5 w-5" />} />
                    </FormControl>

                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={'password'}
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-2">
                    <FormControl>
                      <Input
                        {...field}
                        id="password"
                        name="password"
                        type={isShowingPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        required
                        label="Senha"
                        disabled={isLoading}
                        labelClass="text-foreground font-medium"
                        leftIcon={<Lock className="h-5 w-5" />}
                        rightIcon={<>{isShowingPassword ? <EyeOff className="w-5 h-5 hover:cursor-pointer z-[100]" onClick={() => setIsShowingPassword(prev => !prev)} /> : <Eye className="w-5 h-5 hover:cursor-pointer z-[100]" onClick={() => setIsShowingPassword(prev => !prev)} />}</>}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Button type="button" variant="primary" className="w-full" isLoading={isLoading} onClick={handleLogin} leftIcon={<LogIn className="h-5 w-5" />}>
              Entrar
            </Button>

            <div className="flex items-center justify-center">
              <div className="text-sm">
                <Link to="/forgot-password">
                  <span className="text-muted-foreground hover:text-primary hover:cursor-pointer transition-colors">Esqueceu sua senha?</span>
                </Link>
              </div>
            </div>

            <div className="text-center">
              <p className="text-sm text-muted-foreground">
                Não tem uma conta?
                <span className="text-primary font-semibold hover:brightness-110 hover:cursor-pointer ml-1 transition-all" onClick={() => setIsShowingSignupModal(true)}>
                  Inscrever-se
                </span>
              </p>
            </div>
          </form>
        </Form>
      </div>

      {isShowingSignupModal && (
        <CustomDialog
          open={isShowingSignupModal}
          persistent={true}
          title="Conheça nossos planos"
          confirmationBeforeClose={false}
          onClose={() => {
            setIsShowingSignupModal(false)
          }}
          className="min-w-[1000px]"
        >
          <div className="flex flex-row smAndDown:flex-wrap items-start justify-center gap-5 pointer">
            {products.map(product => (
              <div className="flex flex-col items-center justify-center">
                <ProductCard key={product.id} product={product} />
              </div>
            ))}
          </div>
        </CustomDialog>
      )}
    </div>
  )
}
